import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Check, X, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BulkActionsBarProps {
  selectedCount: number;
  onBulkApprove: () => Promise<void>;
  onBulkReject: () => Promise<void>;
  onBulkDelete?: () => Promise<void>;
  onClearSelection: () => void;
  canEdit?: boolean;
}

export function BulkActionsBar({
  selectedCount,
  onBulkApprove,
  onBulkReject,
  onBulkDelete,
  onClearSelection,
  canEdit = true,
}: BulkActionsBarProps) {
  const { t } = useTranslation('reviews');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleBulkAction = async (action: () => Promise<void>) => {
    setIsProcessing(true);
    setProgress(0);

    // Simulate progress
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 10, 90));
    }, 100);

    try {
      await action();
      setProgress(100);
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      clearInterval(interval);
      setTimeout(() => {
        setIsProcessing(false);
        setProgress(0);
      }, 500);
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="sticky bottom-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">
              {t('bulk.selected', { count: selectedCount })}
            </span>
            {isProcessing && (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div className="w-48">
                  <Progress value={progress} className="h-2" />
                </div>
                <span className="text-sm text-muted-foreground">{progress}%</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearSelection}
              disabled={isProcessing}
            >
              {t('bulk.clearSelection')}
            </Button>
            <Button
              size="sm"
              onClick={() => handleBulkAction(onBulkApprove)}
              disabled={isProcessing || !canEdit}
              className={cn('bg-green-600 hover:bg-green-700', !canEdit && 'opacity-40 pointer-events-none')}
            >
              <Check className="h-4 w-4 mr-2" />
              {t('bulk.approveAll')}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleBulkAction(onBulkReject)}
              disabled={isProcessing || !canEdit}
              className={cn(!canEdit && 'opacity-40 pointer-events-none')}
            >
              <X className="h-4 w-4 mr-2" />
              {t('bulk.rejectAll')}
            </Button>
            {onBulkDelete && (
              <Button
                size="sm"
                onClick={() => setConfirmDelete(true)}
                disabled={isProcessing || !canEdit}
                className={cn(
                  'bg-gray-200 hover:bg-gray-300 text-gray-900',
                  !canEdit && 'opacity-40 pointer-events-none'
                )}
              >
                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                {t('bulk.deleteAll')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('confirm.bulkDeleteTitle', { count: selectedCount })}
        description={t('confirm.bulkDeleteDescription')}
        confirmLabel={t('confirm.deleteConfirm')}
        cancelLabel={t('confirm.cancel')}
        variant="destructive"
        onConfirm={() => {
          if (onBulkDelete) handleBulkAction(onBulkDelete);
        }}
      />
    </div>
  );
}
